import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FiArrowLeft, FiHelpCircle } from 'react-icons/fi';
import { Card, CardContent } from '@/components/ui/card';
import { OfferRideReview } from './OfferRideReview';
import { RideStopLocationStep } from './RideStopLocationStep';
import { RidePreferencesChargeStep } from './RidePreferencesChargeStep';
import { PreferencesDisplay } from './PreferencesDisplay';
import { PricingExplanationModal } from './PricingExplanationModal';

interface StopLocation {
  id: string;
  name: string;
  coordinates: [number, number];
  type: 'pickup' | 'enroute' | 'dropoff';
}

interface RideData {
  from: string;
  to: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  car: string;
  price: number;
  preferences: string[];
  seats: number;
  seatPrefs: string;
  baggage: string;
  note: string;
  stopLocations: StopLocation[];
  preferencesCharges?: { [key: string]: number };
}

export const OfferRidePreviousFlow: React.FC<{
  draftOffer: RideData;
  onBack: () => void;
}> = ({ draftOffer, onBack }) => {
  const [rideData, setRideData] = useState<RideData>({ 
    ...draftOffer,
    stopLocations: draftOffer.stopLocations || [],
    preferencesCharges: draftOffer.preferencesCharges || {}
  });
  const [showReview, setShowReview] = useState(false);
  const [showStopSelection, setShowStopSelection] = useState(false);
  const [showPreferencesCharge, setShowPreferencesCharge] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [originalData] = useState<RideData>({ ...draftOffer });

  // Check if data has changed for button coloring
  const hasChanged = (field: keyof RideData) => {
    if (Array.isArray(rideData[field])) {
      return JSON.stringify(rideData[field]) !== JSON.stringify(originalData[field]);
    }
    return rideData[field] !== originalData[field];
  };

  const hasStopLocations = rideData.stopLocations && rideData.stopLocations.length > 0;

  if (showStopSelection) {
    return (
      <RideStopLocationStep
        from={rideData.from}
        to={rideData.to}
        selectedStops={rideData.stopLocations}
        onStopsSelected={(stops) => setRideData(rd => ({ ...rd, stopLocations: stops }))}
        onBack={() => setShowStopSelection(false)}
        onComplete={() => setShowStopSelection(false)}
      />
    );
  }

  if (showPreferencesCharge) {
    return (
      <RidePreferencesChargeStep
        preferences={rideData.preferences}
        currentCharges={rideData.preferencesCharges || {}}
        onChargesSet={(charges) => setRideData(rd => ({ ...rd, preferencesCharges: charges }))}
        onBack={() => setShowPreferencesCharge(false)}
        onComplete={() => setShowPreferencesCharge(false)}
      />
    );
  }

  if (showReview) {
    return (
      <OfferRideReview
        rideData={rideData}
        onEdit={(field) => {
          if (field === 'stops') {
            setShowStopSelection(true);
          } else if (field === 'preferences-charge') {
            setShowPreferencesCharge(true);
          }
          setShowReview(false);
        }}
        onBack={onBack}
      />
    );
  }

  const handleContinueToReview = () => {
    setShowReview(true);
  };

  return (
    <div className="fixed inset-0 z-[101] bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <Button variant="ghost" onClick={onBack} size="sm" className="p-2">
          <FiArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-gray-800">Quick Edit Ride</h2>
        <div className="w-10"></div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Chalo AI Assistance */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <FiHelpCircle className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-800 mb-1">Chalo AI Assistance</h4>
                <p className="text-sm text-blue-700">
                  Quickly edit your ride information below. Tap any section to modify - date, time, price, or preferences. 
                  Changed items will be highlighted for your convenience.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Route Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Route</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="From"
                  value={rideData.from}
                  onChange={e => setRideData(rd => ({ ...rd, from: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">To</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="To"
                  value={rideData.to}
                  onChange={e => setRideData(rd => ({ ...rd, to: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ride Stops Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Ride Stops</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {rideData.stopLocations.length > 0 
                  ? `${rideData.stopLocations.length} stops selected (Optional)`
                  : 'No stops selected (Optional)'}
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowStopSelection(true)}
              >
                {rideData.stopLocations.length > 0 ? 'Edit Stops' : 'Add Stops'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Date & Time</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date</label>
                <input
                  type="date"
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${hasChanged('date') ? 'border-[#22C55E] bg-orange-50' : ''}`}
                  value={rideData.date}
                  onChange={e => setRideData(rd => ({ ...rd, date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Time</label>
                <input
                  type="time"
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${hasChanged('time') ? 'border-[#22C55E] bg-orange-50' : ''}`}
                  value={rideData.time}
                  onChange={e => setRideData(rd => ({ ...rd, time: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Car Selection */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Vehicle</h3>
            <div className="space-y-2">
              <Button
                variant={rideData.car === 'Honda City - MH12AB1234' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setRideData(rd => ({ ...rd, car: 'Honda City - MH12AB1234' }))}
              >
                Honda City - MH12AB1234
              </Button>
              <Button
                variant={rideData.car === 'Maruti Swift - MH12CD5678' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setRideData(rd => ({ ...rd, car: 'Maruti Swift - MH12CD5678' }))}
              >
                Maruti Swift - MH12CD5678
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Price Section with AI Explanation */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Price per Passenger</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowPricingModal(true)}
                className="text-blue-600"
              >
                <FiHelpCircle className="w-4 h-4 mr-1" />
                How is this calculated?
              </Button>
            </div>
            <div className="space-y-3">
              <input 
                type="range" 
                min={100} 
                max={350} 
                step={10}
                value={rideData.price || 250}
                className={`w-full ${hasChanged('price') ? 'accent-[#22C55E]' : ''}`}
                onChange={e => setRideData(rd => ({ ...rd, price: +e.target.value }))}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>₹100</span>
                <span className={`font-semibold ${hasChanged('price') ? 'text-[#22C55E]' : 'text-gray-800'}`}>
                  ₹{rideData.price || 250}
                </span>
                <span>₹350</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-700">
                  AI Suggested: ₹{rideData.price} (Range: ₹200-₹300)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Preferences</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {['Music', 'AC', 'No Smoking', 'Snacks'].map(pref => (
                <Button
                  key={pref}
                  variant={rideData.preferences.includes(pref) ? 'default' : 'outline'}
                  size="sm"
                  className={hasChanged('preferences') && rideData.preferences.includes(pref) ? 'bg-[#22C55E] hover:bg-[#16A34A]' : ''}
                  onClick={() => setRideData(rd => ({
                    ...rd,
                    preferences: rd.preferences.includes(pref)
                      ? rd.preferences.filter(p => p !== pref)
                      : [...rd.preferences, pref]
                  }))}
                >
                  {pref}
                </Button>
              ))}
            </div>
            {rideData.preferences.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowPreferencesCharge(true)}
              >
                Set Preference Charges (Optional)
              </Button>
            )}
            <PreferencesDisplay 
              preferences={rideData.preferences}
              preferencesCharges={rideData.preferencesCharges}
              showCharges={true}
            />
          </CardContent>
        </Card>

        {/* Seats Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Seats</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Available Seats</label>
                <input 
                  type="number" 
                  min={1} 
                  max={6}
                  value={rideData.seats}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${hasChanged('seats') ? 'border-[#22C55E] bg-orange-50' : ''}`}
                  onChange={e => setRideData(rd => ({ ...rd, seats: +e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Seat Preferences (Optional)</label>
                <input
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${hasChanged('seatPrefs') ? 'border-[#22C55E] bg-orange-50' : ''}`}
                  placeholder="e.g., Front seat only"
                  value={rideData.seatPrefs}
                  onChange={e => setRideData(rd => ({ ...rd, seatPrefs: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Baggage Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Baggage</h3>
            <input
              className={`w-full px-3 py-2 border rounded-lg text-sm ${hasChanged('baggage') ? 'border-[#22C55E] bg-orange-50' : ''}`}
              placeholder="Baggage allowance (e.g., Small bag only)"
              value={rideData.baggage}
              onChange={e => setRideData(rd => ({ ...rd, baggage: e.target.value }))}
            />
          </CardContent>
        </Card>

        {/* Note Section */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Additional Note</h3>
            <textarea
              className={`w-full px-3 py-2 border rounded-lg text-sm resize-none ${hasChanged('note') ? 'border-[#22C55E] bg-orange-50' : ''}`}
              placeholder="Any additional information for passengers"
              rows={3}
              value={rideData.note}
              onChange={e => setRideData(rd => ({ ...rd, note: e.target.value }))}
            />
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Button */}
      <div className="p-4 border-t bg-white">
        <Button 
          onClick={handleContinueToReview}
          className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-3 text-base font-semibold"
          disabled={!rideData.from || !rideData.to}
        >
          Go to Review
        </Button>
      </div>

      {/* Pricing Explanation Modal */}
      <PricingExplanationModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        suggestedPrice={rideData.price || 250}
        minPrice={200}
        maxPrice={300}
        distance={95}
        vehicleType={rideData.car}
      />
    </div>
  );
};
